import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useForm, FormProvider } from 'react-hook-form';
import { BasicTableHead, BasicTableHeadProps } from '../../../components/molecules';
import { MenuHeaderTemplate } from '../../../components/templates/MenuHeaderTemplate';
import { KeyboardDatePickerController } from '../../../components/organisms/form/controllers/KeyboardDatePickerController';
import { ReadMoreButton } from '../../../components/organisms/general/ReadMoreButton';
import { Search } from '@material-ui/icons';
import { useCouponUsageStatisticDetail } from '../hooks/useCouponUsageStatisticDetail';
import { formatJstDate, validateStartEnd } from '../../../util';
import { useSnackbar } from 'notistack';
import { DateTime } from 'luxon';
import { useUrlSearchParam } from '../../../components/contexts/hooks/url';

const useStyles = makeStyles((theme: Theme) => ({
  conditionFormControl: {
    minWidth: 150,
    maxWidth: 300,
  },
  searchForm: {
    marginTop: theme.spacing(1),
  },
  searchButton: {
    textAlign: 'center',
  },
  resultList: {
    marginTop: theme.spacing(3),
  },
  groupButton: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonSearch: {
    width: '232px',
  },
  buttonExport: {
    width: '232px',
    marginLeft: '44px',
  },
}));

export type CouponUsageStatisticDetailType = {
  id: string;
  couponCode: string;
  couponName: string;
  barCode: string;
  url: string;
  datetime: string;
  storeName: string;
};

export type SearchFormValues = {
  usageStartDate: Date | null;
  usageEndDate: Date | null;
};

export const CouponUsageStatisticDetailPage: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { couponMasterId } = useParams<{ couponMasterId: string }>();

  const searchParams = useUrlSearchParam();

  const props = useCouponUsageStatisticDetail({
    couponMasterId,
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const searchForm = useForm<SearchFormValues>({
    defaultValues: {
      usageStartDate: searchParams.get('usageStartDate') ? new Date(searchParams.get('usageStartDate')!) : null,
      usageEndDate: searchParams.get('usageEndDate') ? new Date(searchParams.get('usageEndDate')!) : null,
    },
  });
  const [couponName, setCouponName] = useState<string>('');

  const { handleSubmit, watch } = searchForm;
  const tableHeadProps: BasicTableHeadProps = {
    columns: [
      {
        name: 'couponCode',
        label: 'クーポンコード',
        minWidth: theme.spacing(18),
        align: 'left',
      },
      {
        name: 'couponName',
        label: 'クーポン名',
        minWidth: theme.spacing(30),
        align: 'left',
      },
      {
        name: 'barCode',
        label: 'バーコード',
        minWidth: theme.spacing(18),
        align: 'left',
      },
      {
        name: 'url',
        label: 'URL',
        minWidth: theme.spacing(30),
        align: 'left',
      },
      {
        name: 'datetime',
        label: '利用日時',
        minWidth: theme.spacing(20),
        align: 'left',
      },
      {
        name: 'storeName',
        label: '利用店舗',
        minWidth: theme.spacing(18),
        align: 'left',
      },
    ],
  };

  const validateRangeDatePicker = (data: SearchFormValues): boolean => {
    const usageStartDate = new Date(data.usageStartDate!);
    const usageEndDate = new Date(data.usageEndDate!);

    const usageStartDateTime = DateTime.fromJSDate(usageStartDate);
    const usageEndDateTime = DateTime.fromJSDate(usageEndDate);

    if (!usageStartDateTime.isValid || !usageEndDateTime.isValid) {
      const key = enqueueSnackbar('表示期間、有効期限、取得期限の日時情報を正しく入力してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }

    if (data.usageStartDate && data.usageEndDate && !validateStartEnd(usageStartDateTime, usageEndDateTime, true)) {
      const key = enqueueSnackbar('開始日時が前になるように修正してください', {
        variant: 'warning',
        persist: true,
        onClick: () => closeSnackbar(key),
      });
      return false;
    }
    return true;
  };

  const onSearch = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onSearch(data);
  };

  const onCsvOut = (data: SearchFormValues): void => {
    if (!validateRangeDatePicker(data)) return;
    props.onCsvOut(data);
  };

  useEffect(() => {
    if (!couponName) {
      setCouponName(props.data?.nodes?.[0]?.couponName || '');
    }
  }, [props.data?.nodes]);

  useEffect(() => {
    // init search
    props.onSearch(searchForm.getValues());
  }, []);

  return (
    <MenuHeaderTemplate
      header={{
        title: 'クーポン利用状況一覧',
        loading: undefined,
        progressLabel: undefined,
      }}
    >
      <Container maxWidth="xl">
        <Box mb={3}>
          <Breadcrumbs>
            <Link
              href="#"
              onClick={() =>
                history.push({
                  pathname: '/coupon-usage-statistic',
                  search: history.location.search,
                })
              }
            >
              クーポン利用状況集計
            </Link>
            <Typography variant="body1" color="textPrimary">
              {searchParams.get('couponName') !== couponName && couponName
                ? `${couponName}`
                : `${searchParams.get('couponName')}`}
            </Typography>
          </Breadcrumbs>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormProvider {...searchForm}>
              <form onSubmit={handleSubmit(onSearch)} autoComplete="off" noValidate>
                <Grid container spacing={2}>
                  <Grid container item xs={12} spacing={2} alignItems="center">
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="usageStartDate"
                        label="利用日時"
                        maxDate={
                          watch('usageEndDate') &&
                          new Date(watch('usageEndDate')!) instanceof Date &&
                          !isNaN(new Date(watch('usageEndDate')!).getTime())
                            ? formatJstDate(new Date(watch('usageEndDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item>
                      <Box sx={{ fontSize: '2rem', height: '24px' }}>-</Box>
                    </Grid>
                    <Grid item>
                      <KeyboardDatePickerController
                        required={false}
                        name="usageEndDate"
                        label="利用日時"
                        minDate={
                          watch('usageStartDate') &&
                          new Date(watch('usageStartDate')!) instanceof Date &&
                          !isNaN(new Date(watch('usageStartDate')!).getTime())
                            ? formatJstDate(new Date(watch('usageStartDate')!).toISOString())
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} spacing={2} alignItems="center">
                    <Grid item>
                      <Box className={classes.groupButton}>
                        <Button
                          type="submit"
                          variant={'outlined'}
                          size={'medium'}
                          color={'default'}
                          fullWidth
                          endIcon={<Search />}
                          className={classes.buttonSearch}
                          disabled={props.loading || props.csvLoading}
                        >
                          検索
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          size="medium"
                          color={'secondary'}
                          fullWidth
                          className={classes.buttonExport}
                          onClick={handleSubmit(onCsvOut)}
                          disabled={props.loading || props.csvLoading}
                        >
                          {props.csvLoading ? <CircularProgress size={26} /> : 'CSV出力'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Grid>

          <Grid item xs={12} className={classes.resultList}>
            <TableContainer component={Paper}>
              {props.loading ? <LinearProgress /> : <React.Fragment />}
              <Table>
                <BasicTableHead columns={tableHeadProps.columns} />
                <TableBody>
                  {(() => {
                    if (!props.data) {
                      return;
                    } else if (!props.data || props.data?.nodes?.length === 0) {
                      return (
                        <TableRow>
                          <TableCell>データがありません</TableCell>
                        </TableRow>
                      );
                    } else {
                      return props.data?.nodes?.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponCode}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.couponName}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {record.barcode}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {record.url}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {record.createAtMillis}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{
                              wordBreak: 'break-all',
                            }}
                          >
                            {record.usedShopName}
                          </TableCell>
                        </TableRow>
                      ));
                    }
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
        {props.data?.nodes?.length && !props.allAcquired ? (
          <Box mt={2}>
            <ReadMoreButton
              fetchMore={() => {
                props.onFetchMore({
                  usageStartDate: searchForm.getValues('usageStartDate'),
                  usageEndDate: searchForm.getValues('usageEndDate'),
                });
              }}
              loading={props.loading}
            />
          </Box>
        ) : (
          <></>
        )}
      </Container>
    </MenuHeaderTemplate>
  );
};
